// src/pages/admin/AdminSettings.jsx
import React, { useState, useEffect } from "react";
import { EMS_DEPARTMENTS } from "../../utils/hrConstants";
import { useOrgSettings, useDepartments, addDepartment, toggleDepartmentActive, deleteDepartment } from "../../hooks/useFirestore";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { createOrgSettings } from "../../firebase/schema";
import { seedEmployeeMasterData } from "../../hooks/useEmployeeMaster";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

export default function AdminSettings() {
  const { data: settings, loading } = useOrgSettings();
  const [form, setForm] = useState({
    orgName: "Samriddhi Anveshana",
    workHoursPerDay: 8,
    financialYearStart: "April",
    leavePolicy: {
      casual: 12,
      sick: 12,
      annual: 21,
      autoApproveAfterDays: 3,
      maxCarryForward: 10,
    },
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({
        orgName: settings.orgName || "Samriddhi Anveshana",
        workHoursPerDay: settings.workHoursPerDay || 8,
        financialYearStart: settings.financialYearStart || "April",
        leavePolicy: settings.leavePolicy || {
          casual: 12,
          sick: 12,
          annual: 21,
          autoApproveAfterDays: 3,
          maxCarryForward: 10,
        },
      });
    }
  }, [settings]);

  function handleChange(e) {
  const { name, value } = e.target;

  if (name.startsWith("lp_")) {
    const field = name.replace("lp_", "");

    setForm((p) => ({
      ...p,
      leavePolicy: {
        ...p.leavePolicy,
        [field]: Number(value),
      },
    }));
  } else {
    setForm((p) => ({
      ...p,
      [name]:
        name === "workHoursPerDay"
          ? Number(value)
          : value,
    }));
  }
}

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    try {
      await setDoc(doc(db, "orgSettings", "config"), {
        ...form,
        updatedAt: serverTimestamp(),
      });
      setMsg("✅ Settings saved!");
    } catch (err) {
      setMsg("❌ " + err.message);
    }
    setSaving(false);
  }
  // One-time seed function — run once on fresh deploy
  async function handleSeedDefaults() {
    setSeeding(true);
    try {
      await createOrgSettings();
      await seedEmployeeMasterData();
      setMsg("✅ Default org settings and employee master lookup data initialized!");
    } catch (err) {
      setMsg("❌ " + err.message);
    }
    setSeeding(false);
  }

  // ── Department Management State & Actions ───────────────
  const { departments, loading: deptsLoading } = useDepartments();
  const [newDeptName, setNewDeptName] = useState("");
  const [deptMsg, setDeptMsg] = useState("");
  const [addingDept, setAddingDept] = useState(false);

  const handleSeedDepartments = async () => {
    setAddingDept(true);
    setDeptMsg("");
    try {
      const defaultDepts = EMS_DEPARTMENTS;
      for (const d of defaultDepts) {
        if (!departments.some(x => x.name.toLowerCase() === d.toLowerCase())) {
          await addDepartment(d);
        }
      }
      setDeptMsg("✅ Seeded default departments successfully!");
    } catch (err) {
      setDeptMsg("❌ " + err.message);
    } finally {
      setAddingDept(false);
    }
  };

  const handleAddDept = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) return;
    setAddingDept(true);
    setDeptMsg("");
    try {
      if (departments.some(d => d.name.toLowerCase() === newDeptName.trim().toLowerCase())) {
        throw new Error("Department already exists.");
      }
      await addDepartment(newDeptName);
      setNewDeptName("");
      setDeptMsg("✅ Department added successfully!");
    } catch (err) {
      setDeptMsg("❌ " + err.message);
    } finally {
      setAddingDept(false);
    }
  };

  const handleToggleDept = async (deptId, currentActive) => {
    try {
      await toggleDepartmentActive(deptId, !currentActive);
    } catch (err) {
      setDeptMsg("❌ " + err.message);
    }
  };

  const handleDeleteDept = async (deptId) => {
    if (!window.confirm("Are you sure you want to delete this department? Employees assigned to this department will not be automatically updated.")) return;
    try {
      await deleteDepartment(deptId);
      setDeptMsg("✅ Department deleted successfully!");
    } catch (err) {
      setDeptMsg("❌ " + err.message);
    }
  };


  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      {!settings && !loading && (
        <div className="form-card" style={{ marginBottom: "1.5rem" }}>
          <h3 className="form-card-title">🚀 First-Time Setup</h3>
          <p style={{ marginBottom: "1rem", color: "#666" }}>
            No org settings found. Click below to create default settings.
          </p>
          <button
            className="btn-primary"
            onClick={handleSeedDefaults}
            disabled={seeding}
          >
            {seeding ? "Creating…" : "Initialize Default Settings"}
          </button>
        </div>
      )}

      <div className="two-col-layout">
        <div className="form-card">
          <h3 className="form-card-title">Organization Settings</h3>
          {msg && (
            <div
              className={msg.startsWith("✅") ? "form-success" : "form-error"}
            >
              {msg}
            </div>
          )}
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Organization Name</label>
              <input
                name="orgName"
                value={form.orgName}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Work Hours / Day</label>
                <input
                  name="workHoursPerDay"
                  type="number"
                  value={form.workHoursPerDay}
                  onChange={handleChange}
                  min={1}
                  max={24}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Financial Year Start</label>
              <select
                name="financialYearStart"
                value={form.financialYearStart}
                onChange={handleChange}
              >
                {[
                  "January",
                  "February",
                  "March",
                  "April",
                  "July",
                  "October",
                ].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Settings"}
            </button>
          </form>
        </div>

        <div className="form-card">
          <h3 className="form-card-title">Leave Policy</h3>
          <div className="form-grid">
            {[
              { key: "casual", label: "Casual Leave (days/year)" },
              { key: "sick", label: "Sick Leave (days/year)" },
              { key: "annual", label: "Annual Leave (days/year)" },
              {
                key: "autoApproveAfterDays",
                label: "Auto-approve after (days)",
              },
              { key: "maxCarryForward", label: "Max carry-forward (days)" },
            ].map(({ key, label }) => (
              <div key={key} className="form-group">
                <label>{label}</label>
                <input
                  name={`lp_${key}`}
                  type="number"
                  value={form.leavePolicy[key] ?? 0}
                  onChange={handleChange}
                  min={0}
                />
              </div>
            ))}
            <div className="form-group form-group--full">
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Update Policy"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Department Management ────────────────────────────── */}
      <div className="form-card" style={{ marginTop: "1.5rem" }}>
        <h3 className="form-card-title">Department Management</h3>
        <p className="page-subtitle" style={{ marginBottom: "1.5rem", color: "#666" }}>
          Manage departments dynamically. Active departments will automatically populate dropdown lists across the application in real-time.
        </p>

        {deptMsg && (
          <div className={deptMsg.startsWith("✅") ? "form-success" : "form-error"} style={{ marginBottom: "1.5rem" }}>
            {deptMsg}
          </div>
        )}

        <div className="two-col-layout" style={{ gap: "2rem", gridTemplateColumns: "1fr 2fr" }}>
          <div>
            <h4 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "0.75rem", color: "var(--text-primary)" }}>Add New Department</h4>
            <form onSubmit={handleAddDept}>
              <div className="form-group">
                <label>Department Name</label>
                <input
                  type="text"
                  placeholder="e.g. AI & ML, Sales"
                  value={newDeptName}
                  onChange={(e) => setNewDeptName(e.target.value)}
                  disabled={addingDept}
                />
              </div>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button type="submit" className="btn-primary" disabled={addingDept || !newDeptName.trim()}>
                  {addingDept ? "Adding..." : "Add"}
                </button>
                {departments.length === 0 && (
                  <button type="button" className="btn-secondary" onClick={handleSeedDepartments} disabled={addingDept} style={{ background: "transparent", border: "1px solid var(--border)", padding: "0.5rem 1rem", borderRadius: "var(--radius)", color: "var(--text-primary)" }}>
                    Seed Defaults
                  </button>
                )}
              </div>
            </form>
          </div>

          <div>
            <h4 style={{ fontSize: "1rem", fontWeight: "600", marginBottom: "0.75rem", color: "var(--text-primary)" }}>Active & Scale Departments</h4>
            {deptsLoading ? (
              <p>Loading departments...</p>
            ) : departments.length === 0 ? (
              <div className="card-empty" style={{ padding: "2rem", textAlign: "center" }}>
                <p style={{ marginBottom: "1rem" }}>No departments found.</p>
                <button type="button" className="btn-primary" onClick={handleSeedDepartments}>
                  Seed Default Departments
                </button>
              </div>
            ) : (
              <div className="data-table-wrap" style={{ maxHeight: "300px", overflowY: "auto" }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Department Name</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((dept) => (
                      <tr key={dept.id}>
                        <td style={{ fontWeight: "600" }}>{dept.name}</td>
                        <td>
                          <span
                            className={`status-pill ${dept.active ? "status-pill--green" : "status-pill--red"}`}
                            onClick={() => handleToggleDept(dept.id, dept.active)}
                            style={{ cursor: "pointer", userSelect: "none" }}
                            title="Click to toggle status"
                          >
                            {dept.active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-text-danger"
                            onClick={() => handleDeleteDept(dept.id)}
                            style={{ color: "var(--red, #ef4444)", background: "none", border: "none", cursor: "pointer", fontSize: "0.85rem", fontWeight: "600" }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
